import {
  CandlestickData,
  IndicatorUnion,
  SignalData,
  TechnicalIndicatorSignal,
  TradeTriggers,
  Trade,
  Timeframe
} from '@server/data-feed/schemas/output.schema';

// Helpers for time-based data merging
const getLastItemTime = <T extends { time: string }>(
  items: T[]
): string | null => (items.length > 0 ? items[items.length - 1].time : null);

export const mergeTimeBasedArray = <T extends { time: string }>(
  existing: T[],
  updates: T[]
): T[] => {
  const merged = [...existing];

  for (const update of updates) {
    const lastExistingTime = getLastItemTime(merged);

    if (lastExistingTime === update.time) {
      merged[merged.length - 1] = update;
    } else {
      merged.push(update);
    }
  }

  return merged;
};

const mergeCandles = (
  existing: CandlestickData[],
  updates: CandlestickData[]
): CandlestickData[] => mergeTimeBasedArray(existing, updates);

export const mergeTrades = (existing: Trade[], updates: Trade[]): Trade[] =>
  mergeTimeBasedArray(existing, updates);

export const mergeSignals = (
  existingSignals: TechnicalIndicatorSignal[],
  newSignals: TechnicalIndicatorSignal[]
): TechnicalIndicatorSignal[] => {
  return existingSignals.map((existingSignal, index) => {
    const newSignal = newSignals[index];
    return {
      ...existingSignal,
      data: mergeSignalData(existingSignal.data, newSignal.data)
    };
  });
};

const mergeSignalData = (
  existingData: SignalData[],
  newData: SignalData[]
): SignalData[] => {
  const merged = [...existingData];

  newData.forEach((newItem) => {
    const lastItem = merged[merged.length - 1];

    if (lastItem?.time === newItem.time) {
      merged[merged.length - 1] = newItem;
    } else {
      merged.push(newItem);
    }
  });

  return merged;
};

const mergeTradeTriggers = (
  existing: TradeTriggers | undefined,
  updates: TradeTriggers
) => ({
  long: {
    entryTriggers: mergeTimeBasedArray(
      existing?.long?.entryTriggers || [],
      updates?.long?.entryTriggers || []
    ),
    exitTriggers: mergeTimeBasedArray(
      existing?.long?.exitTriggers || [],
      updates?.long?.exitTriggers || []
    )
  },
  short: {
    entryTriggers: mergeTimeBasedArray(
      existing?.short?.entryTriggers || [],
      updates?.short?.entryTriggers || []
    ),
    exitTriggers: mergeTimeBasedArray(
      existing?.short?.exitTriggers || [],
      updates?.short?.exitTriggers || []
    )
  }
});

const mergeIndicators = (
  existing: IndicatorUnion[] = [],
  updates: IndicatorUnion[] = []
): IndicatorUnion[] => {
  const stripIndicatorData = <T extends IndicatorUnion>(indicator: T): T => ({
    ...indicator,
    data: [] as T['data']
  });

  // Refactored indicators are rendered from dedicated update streams, not from
  // context payload arrays. Keep only indicator config in context state.
  const merged: IndicatorUnion[] = [];
  const maxLength = Math.max(existing.length, updates.length);

  for (let index = 0; index < maxLength; index += 1) {
    const indicator = updates[index] ?? existing[index];
    if (!indicator) continue;
    merged.push(stripIndicatorData(indicator));
  }

  return merged;
};

export const mergeTimeframes = (
  existing: Timeframe[],
  updates: Timeframe[]
): Timeframe[] => {
  return existing.map((_, index) => {
    const tfExisting = existing[index];
    const tfUpdates = updates[index];

    const mergedIndicators =
      tfExisting.indicators || tfUpdates.indicators
        ? mergeIndicators(
            tfExisting.indicators || [],
            tfUpdates.indicators || []
          )
        : undefined;

    const mergedSignals =
      tfExisting.signals || tfUpdates.signals
        ? mergeSignals(tfExisting.signals || [], tfUpdates.signals || [])
        : undefined;

    return {
      candlesticks: mergeCandles(
        tfExisting.candlesticks,
        tfUpdates.candlesticks
      ),
      indicators: mergedIndicators,
      signals: mergedSignals,
      tradeTriggers: tfUpdates.tradeTriggers
        ? mergeTradeTriggers(
            tfExisting.tradeTriggers || undefined,
            tfUpdates.tradeTriggers
          )
        : tfExisting.tradeTriggers,
      tradeTriggersConfluence: tfUpdates.tradeTriggersConfluence
        ? mergeTradeTriggers(
            tfExisting.tradeTriggersConfluence || undefined,
            tfUpdates.tradeTriggersConfluence
          )
        : tfExisting.tradeTriggersConfluence
    };
  });
};
